import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import anime from 'animejs';
import { FaChevronDown } from 'react-icons/fa';
import './Hero.css';

const Hero = () => {
  const heroRef = useRef(null);
  const morphingShapeRef = useRef(null);

  useEffect(() => {
    // Anime.js text animation
    anime({
      targets: '.hero-title .word',
      translateY: [100, 0],
      opacity: [0, 1],
      easing: 'easeOutExpo',
      duration: 1400,
      delay: (el, i) => 50 * i
    });

    // Anime.js subtitle animation
    anime({
      targets: '.hero-subtitle',
      translateY: [50, 0],
      opacity: [0, 1],
      easing: 'easeOutExpo',
      duration: 1000,
      delay: 800
    });

    // Anime.js button animation
    anime({
      targets: '.hero-buttons .btn',
      scale: [0, 1],
      opacity: [0, 1],
      easing: 'easeOutElastic(1, .6)',
      duration: 1500,
      delay: (el, i) => 1200 + 100 * i
    });

    // Morphing shape animation
    if (morphingShapeRef.current) {
      anime({
        targets: morphingShapeRef.current,
        d: [
          { value: 'M50,10 C70,10 90,30 90,50 C90,70 70,90 50,90 C30,90 10,70 10,50 C10,30 30,10 50,10' },
          { value: 'M50,0 C75,0 100,25 100,50 C100,75 75,100 50,100 C25,100 0,75 0,50 C0,25 25,0 50,0' },
          { value: 'M50,10 C70,10 90,30 90,50 C90,70 70,90 50,90 C30,90 10,70 10,50 C10,30 30,10 50,10' }
        ],
        easing: 'easeInOutQuad',
        duration: 3000,
        loop: true
      });
    }

    // Floating elements animation
    anime({
      targets: '.floating-element',
      translateY: [-20, 20],
      translateX: [-10, 10],
      rotate: [0, 360],
      scale: [0.8, 1.2, 0.8],
      easing: 'easeInOutSine',
      duration: 4000,
      loop: true,
      delay: (el, i) => 200 * i
    });

  }, []);
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10
      }
    }
  };

  const floatingVariants = {
    animate: {
      y: [0, -20, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };

  return (
    <section id="home" className="hero" ref={heroRef}>
      <div className="hero-background">
        <div className="gradient-sphere sphere-1"></div>
        <div className="gradient-sphere sphere-2"></div>
        <div className="gradient-sphere sphere-3"></div>
        
        {/* Animated floating elements */}
        <div className="floating-element element-1"></div>
        <div className="floating-element element-2"></div>
        <div className="floating-element element-3"></div>
        <div className="floating-element element-4"></div>
        
        {/* SVG morphing shape */}
        <svg className="morphing-shape" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#667eea" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#764ba2" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          <path
            ref={morphingShapeRef}
            d="M50,10 C70,10 90,30 90,50 C90,70 70,90 50,90 C30,90 10,70 10,50 C10,30 30,10 50,10"
            fill="url(#gradient)"
            stroke="#667eea"
            strokeWidth="0.5"
          />
        </svg>
      </div>

      <motion.div
        className="container hero-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="hero-content" variants={itemVariants}>
          <motion.div
            className="hero-logo-container"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <img src="/logo.png" alt="Avionverse Logo" className="hero-logo" />
          </motion.div>

          <h1 className="hero-title">
            {['Transform', 'Your', 'Business', 'with'].map((word, index) => (
              <span key={index} className="word">{word} </span>
            ))}
            <br />
            <span className="gradient-text">
              {['AI', 'Automation'].map((word, index) => (
                <span key={index} className="word">{word} </span>
              ))}
            </span>
          </h1>

          <motion.p className="hero-subtitle" variants={itemVariants}>
            Empowering companies with cutting-edge artificial intelligence
            solutions to streamline operations and drive exponential growth
          </motion.p>

          <motion.div className="hero-buttons" variants={itemVariants}>
            <motion.a
              href="#contact"
              className="btn btn-primary"
              whileHover={{ scale: 1.05, boxShadow: '0 10px 40px rgba(102, 126, 234, 0.5)' }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started
            </motion.a>
            <motion.a
              href="#services"
              className="btn btn-secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Explore Services
            </motion.a>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-visual"
          variants={itemVariants}
          animate="animate"
        >
          <motion.div
            className="cube-container"
            animate={{
              rotateX: [0, 360],
              rotateY: [0, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'linear'
            }}
          >
            <div className="cube">
              <div className="cube-face front"></div>
              <div className="cube-face back"></div>
              <div className="cube-face right"></div>
              <div className="cube-face left"></div>
              <div className="cube-face top"></div>
              <div className="cube-face bottom"></div>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        className="scroll-indicator"
        variants={floatingVariants}
        animate="animate"
      >
        <FaChevronDown />
      </motion.div>
    </section>
  );
};

export default Hero;
